import React, { useEffect, useMemo, useState } from "react";
import {
  FiCalendar,
  FiFileText,
  FiPrinter,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import moment from "moment";
import { useSelector } from "react-redux";
import SaleType from "../Input/SaleType";
import InvoiceCustomerType from "../Input/InvoiceCustomerType";
import CustomerInvoicesBrowseTable from "./CustomerInvoicesBrowseTable";
import ThermalCustomerInvoice from "../Reports/ThermalCustomerInvoice";
import ThermalPrintModeSelect from "../Thermal/ThermalPrintModeSelect";
import {
  THERMAL_WIDTH_MM,
  THERMAL_PREVIEW_ROLL_EXTRA_MM,
} from "../Thermal/thermalConfig";
import {
  getInvoicePartyName,
  isGuestInvoice,
} from "../../utils/invoice";
import { mapInvoiceToThermalReceipt } from "../../utils/thermalReceipt";
import { localYmd } from "../Reports/ReportDateRangeBar";

const fieldClass =
  "w-full h-9 px-2.5 rounded-lg border border-line bg-card text-ink text-sm font-semibold outline-none focus:border-main focus:ring-2 focus:ring-main/15";

const matchesInvoiceSearch = (inv, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const party = getInvoicePartyName(inv).toLowerCase();
  const manual = String(inv.invoice_no || "").toLowerCase();
  const auto = String(inv.auto_invoice_no ?? "").toLowerCase();
  return party.includes(q) || manual.includes(q) || auto.includes(q);
};

const StatusBadge = ({ isGuest, isReturn }) => {
  if (isReturn) {
    return (
      <span className="inline-flex items-center rounded-md bg-dangerSoft px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-danger">
        Return
      </span>
    );
  }
  if (isGuest) {
    return (
      <span className="inline-flex items-center rounded-md bg-warningSoft px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-warning">
        Guest
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-brandSoft px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-main">
      Customer
    </span>
  );
};

const InvoiceBrowseSection = ({
  saleType,
  setSaleType,
  partyType,
  setPartyType,
  isGuestParty,
  guestSearch,
  setGuestSearch,
  guestLoading,
  guestInvoices,
  filterCustomerId,
  setFilterCustomerId,
  invoiceListSearch,
  setInvoiceListSearch,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  appliedFromDate,
  appliedToDate,
  onApplyFilter,
  filterLoading = false,
  CustomerInvoices,
  CustomerReturnInvoices,
  setSelectedCustomer,
  SelectedInvoice,
  setSelectedInvoice,
  activeInvoice,
  onPrint,
  onDeleteReturn,
  deleteReturnLoading,
  onDeleteInvoice,
  deleteInvoiceLoading,
  onSelectInvoice,
  onEditInvoice,
  onPrintInvoice,
}) => {
  const isReturn = saleType === "RETURN";
  const Customers = useSelector((state) => state.Customers);
  const searchValue = isGuestParty ? guestSearch : invoiceListSearch;
  const setSearchValue = isGuestParty ? setGuestSearch : setInvoiceListSearch;

  const [printMode, setPrintMode] = useState("full");

  useEffect(() => {
    if (!activeInvoice) return;
    setPrintMode(
      isGuestInvoice(activeInvoice) ? "hideItemPrices" : "full"
    );
  }, [activeInvoice?._id]);

  const sourceInvoices = useMemo(() => {
    const sales = CustomerInvoices.data || [];
    const returns = CustomerReturnInvoices.data || [];
    const base = isReturn ? returns : sales;

    if (!isGuestParty) return base;
    return (guestInvoices || []).length
      ? guestInvoices
      : base.filter(isGuestInvoice);
  }, [
    isReturn,
    isGuestParty,
    CustomerInvoices.data,
    CustomerReturnInvoices.data,
    guestInvoices,
  ]);

  const listLoading =
    filterLoading ||
    (isGuestParty
      ? guestLoading ||
        (isReturn ? CustomerReturnInvoices.loading : CustomerInvoices.loading)
      : isReturn
      ? CustomerReturnInvoices.loading
      : CustomerInvoices.loading);

  const filteredInvoices = useMemo(() => {
    return sourceInvoices
      .filter((inv) => {
        if (isGuestParty) {
          if (!isGuestInvoice(inv)) return false;
          return matchesInvoiceSearch(inv, guestSearch);
        }

        if (filterCustomerId) {
          const cid =
            inv.customerId?._id?.toString() || inv.customerId?.toString() || "";
          if (cid !== filterCustomerId) return false;
        }

        return matchesInvoiceSearch(inv, invoiceListSearch);
      })
      .sort((a, b) => (b.date || 0) - (a.date || 0));
  }, [
    sourceInvoices,
    filterCustomerId,
    invoiceListSearch,
    guestSearch,
    isGuestParty,
  ]);

  const periodLabel =
    appliedFromDate && appliedToDate
      ? appliedFromDate === appliedToDate
        ? moment(appliedFromDate, "YYYY-MM-DD").format("DD MMM YYYY")
        : `${moment(appliedFromDate, "YYYY-MM-DD").format("DD MMM YY")} – ${moment(
            appliedToDate,
            "YYYY-MM-DD"
          ).format("DD MMM YY")}`
      : "—";

  const activeIsGuest =
    Boolean(activeInvoice) &&
    (isGuestParty || isGuestInvoice(activeInvoice));

  const previewReceipt = useMemo(() => {
    if (!activeInvoice) return null;
    const mode =
      printMode ||
      (isGuestInvoice(activeInvoice) ? "hideItemPrices" : "full");
    return mapInvoiceToThermalReceipt(activeInvoice, {
      isReturn,
      printMode: mode,
    });
  }, [activeInvoice, isReturn, printMode]);

  const setTodayRange = () => {
    const today = localYmd();
    setFromDate(today);
    setToDate(today);
  };

  const handleFromChange = (value) => {
    if (!value) return;
    setFromDate(value);
    if (toDate && value > toDate) setToDate(value);
  };

  const handleToChange = (value) => {
    if (!value) return;
    setToDate(value);
    if (fromDate && value < fromDate) setFromDate(value);
  };

  return (
    <div className="flex flex-col gap-3 w-full font-quicksand">
      {/* Controls — compact single strip */}
      <section className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
        <div className="px-3 py-2.5 flex flex-wrap items-end gap-x-3 gap-y-2 border-b border-line">
          <SaleType saleType={saleType} setSaleType={setSaleType} />
          <InvoiceCustomerType
            customerType={partyType}
            setCustomerType={setPartyType}
            label={isReturn ? "Return from" : "Sale to"}
          />
          <span className="inline-flex items-center gap-1.5 rounded-md bg-brandSoft/70 border border-main/15 px-2 py-1 text-[11px] font-semibold text-main ml-auto">
            <FiCalendar size={11} />
            {periodLabel}
            <span className="text-main/60">·</span>
            {filteredInvoices.length} result
            {filteredInvoices.length !== 1 ? "s" : ""}
            {filterLoading ? " · …" : ""}
          </span>
        </div>

        <div className="px-3 py-2.5 flex flex-wrap items-end gap-2">
          <label className="block w-[132px]">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-0.5">
              From
            </span>
            <input
              type="date"
              min="2000-01-01"
              max="2099-12-31"
              value={fromDate || ""}
              onChange={(e) => handleFromChange(e.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="block w-[132px]">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-0.5">
              To
            </span>
            <input
              type="date"
              min="2000-01-01"
              max="2099-12-31"
              value={toDate || ""}
              onChange={(e) => handleToChange(e.target.value)}
              className={fieldClass}
            />
          </label>
          <button
            type="button"
            onClick={setTodayRange}
            className="h-9 px-2.5 rounded-lg border border-line bg-subtle text-inkSecondary text-xs font-bold hover:border-main/40 hover:text-main transition-colors"
          >
            Today
          </button>
          <button
            type="button"
            onClick={onApplyFilter}
            disabled={filterLoading || !fromDate || !toDate}
            className="h-9 px-3.5 rounded-lg bg-main text-white text-xs font-bold hover:bg-mainHover disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
          >
            <FiCalendar size={14} />
            {filterLoading ? "…" : "Apply"}
          </button>
          {!isGuestParty && (
            <label className="block min-w-[140px] flex-1 max-w-[200px]">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-0.5">
                Customer
              </span>
              <select
                value={filterCustomerId}
                onChange={(e) => {
                  setFilterCustomerId(e.target.value);
                  setSelectedInvoice("");
                  setSelectedCustomer("");
                }}
                className={fieldClass}
              >
                <option value="">All customers</option>
                {(Customers.data || []).map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="block min-w-[160px] flex-[1.5]">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-0.5">
              Search
            </span>
            <div className="relative">
              <FiSearch
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
                size={14}
              />
              <input
                type="search"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Invoice # or name…"
                className={`${fieldClass} pl-8`}
              />
            </div>
          </label>
        </div>
      </section>

      {/* List (primary) + preview (narrow) */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px] gap-3 items-start">
        <section className="rounded-xl border border-line bg-card shadow-soft overflow-hidden min-w-0">
          <div className="px-3 py-2 border-b border-line bg-subtle/50 flex items-center justify-between gap-2">
            <h2 className="text-sm font-bold text-ink">
              {isReturn ? "Returns" : "Sales"} list
            </h2>
            <p className="text-[11px] text-muted hidden sm:block">
              Click a row to preview
            </p>
          </div>
          <div className="p-2.5">
            <CustomerInvoicesBrowseTable
              invoices={filteredInvoices}
              loading={listLoading}
              selectedInvoiceId={SelectedInvoice}
              isReturn={isReturn}
              onView={onSelectInvoice}
              onEdit={onEditInvoice}
              onDelete={!isReturn ? onDeleteInvoice : undefined}
              onPrint={onPrintInvoice}
            />
          </div>
        </section>

        <aside className="rounded-xl border border-line bg-card shadow-soft overflow-hidden lg:sticky lg:top-3 min-w-0 max-h-[calc(100vh-7rem)] flex flex-col">
          {!SelectedInvoice || !activeInvoice ? (
            <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
              <FiFileText className="text-main/35 mb-2" size={32} />
              <p className="text-sm font-semibold text-ink">No invoice selected</p>
              <p className="text-xs text-muted mt-1">
                Select a row to preview.
              </p>
            </div>
          ) : listLoading ? (
            <div className="px-4 py-8 text-center text-sm text-muted">
              Loading…
            </div>
          ) : (
            <>
              <div className="px-2.5 py-2 border-b border-line bg-subtle/50 shrink-0 space-y-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <StatusBadge isGuest={activeIsGuest} isReturn={isReturn} />
                  <h2 className="text-xs font-bold text-ink truncate">
                    {activeInvoice.invoice_no
                      ? `#${activeInvoice.invoice_no}`
                      : `Auto #${activeInvoice.auto_invoice_no ?? "—"}`}
                  </h2>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => onEditInvoice(activeInvoice)}
                    className="inline-flex items-center gap-1 h-8 px-2 rounded-md border-2 border-main bg-white text-main text-[11px] font-bold hover:bg-main/5 transition-colors"
                  >
                    <FiFileText size={12} />
                    Edit
                  </button>
                  {!isReturn ? (
                    <button
                      type="button"
                      disabled={deleteInvoiceLoading}
                      onClick={() => onDeleteInvoice(activeInvoice)}
                      className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-danger/30 bg-white text-danger text-[11px] font-bold hover:bg-dangerSoft disabled:opacity-50 transition-colors"
                    >
                      <FiTrash2 size={12} />
                      Delete
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={deleteReturnLoading}
                      onClick={onDeleteReturn}
                      className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-danger/30 bg-white text-danger text-[11px] font-bold hover:bg-dangerSoft disabled:opacity-50 transition-colors"
                    >
                      <FiTrash2 size={12} />
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onPrint}
                    className="inline-flex items-center gap-1 h-8 px-2 rounded-md bg-main text-white text-[11px] font-bold hover:bg-mainHover transition-colors"
                  >
                    <FiPrinter size={12} />
                    Print
                  </button>
                </div>
                <ThermalPrintModeSelect
                  compact
                  value={printMode}
                  onChange={setPrintMode}
                />
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto bg-subtle/60 px-2 py-3">
                <div className="mx-auto w-fit scale-[0.92] origin-top">
                  <div className="relative">
                    <div
                      className="absolute -top-1.5 left-1/2 -translate-x-1/2 h-2.5 rounded-full bg-muted border border-inkSecondary/40"
                      style={{
                        width: `${THERMAL_WIDTH_MM + THERMAL_PREVIEW_ROLL_EXTRA_MM}mm`,
                      }}
                    />
                    <div
                      className="relative bg-white shadow-soft ring-1 ring-black/10"
                      style={{
                        width: `${THERMAL_WIDTH_MM}mm`,
                        minWidth: `${THERMAL_WIDTH_MM}mm`,
                      }}
                    >
                      {previewReceipt && (
                        <ThermalCustomerInvoice data={previewReceipt} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default InvoiceBrowseSection;
